<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();

const confirmDeleteContactDialogRef = ref(null);

const avatarFile = ref(null);
const avatarUrl = ref('');

const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const contactData = ref({});

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
});

const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTime(contactData.value.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return contactData.value?.lastActivityAt
    ? dynamicTime(contactData.value.lastActivityAt)
    : '';
});

const location = computed(() => {
  const attrs = contactData.value?.additionalAttributes || {};
  return [attrs.province, attrs.city, attrs.country].filter(Boolean).join(' · ');
});

// 名片状态: 右侧面板可直接修改。后端 custom_attributes 是 merge, 只发 card_status 不影响其他字段。
// 注意: panelContact 来自 getContactById(stopPaths保留 custom_attributes 为 snake_case), 列表来自
// getContactsList(全 camel)。两种形态都兼容读取; 本地更新写 snake(card_status) 与面板数据一致。
const cardStatusOptions = [
  { value: '', label: '未设置' },
  { value: '未识别已同步', label: '未识别已同步' },
  { value: '已查看无留联', label: '已查看无留联' },
];
// 定制: contactData 里 custom_attributes(蛇形, 来自 getContactById) 与
// customAttributes(驼峰, ContactsForm 可编辑字段 emit 写入) 两套 key 会分裂——
// 填微信只进驼峰、选状态/校验却优先读蛇形(旧值), 互相覆盖导致刚填的微信丢失。
// 统一合并: 蛇形打底, 驼峰覆盖, 所有读写都基于这一份真值, 杜绝分裂。
// (驼峰侧只含 wechat/landline/qq/owner, 与蛇形同名键可直接覆盖; card_status/source 等仍由蛇形提供)
const contactCustomAttrs = computed(() => ({
  ...(contactData.value?.custom_attributes || {}),
  ...(contactData.value?.customAttributes || {}),
}));
const selectedCardStatus = computed(
  () =>
    contactCustomAttrs.value.card_status ||
    contactCustomAttrs.value.cardStatus ||
    ''
);
const cardStatusColorClass = computed(() => {
  const s = selectedCardStatus.value;
  if (s === '未识别已同步') return 'bg-green-500';
  if (s === '已查看无留联') return 'bg-slate-400';
  return 'bg-transparent border border-solid border-n-slate-4';
});
const selectCardStatus = opt => {
  // 定制: 名片状态只更新本地显示, 保存统一走"更新联系人"(updateContact),
  // 以便在点"更新联系人"时校验"未识别已同步"必须至少有一种联系方式。
  const next = { ...contactCustomAttrs.value, card_status: opt };
  contactData.value = {
    ...contactData.value,
    custom_attributes: next,
    customAttributes: next,
  };
};

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const updateContact = async () => {
  // 定制: 点"更新联系人"时, 若名片状态为"未识别已同步"但没有任何联系方式,
  // 则不生效: 回退状态到原值 + 提示, 不保存。
  // ca 用合并视图(蛇形打底+驼峰覆盖), 保证刚在表单里填的微信能被读到(否则误判无联系方式)。
  const ca = contactCustomAttrs.value;
  const cardStatus = ca.card_status || ca.cardStatus;
  if (cardStatus === '未识别已同步') {
    // 手机号同样存在蛇形/驼峰分裂: 表单 emit 写入 phoneNumber(驼峰), 故两种都查。
    const hasContactMethod =
      (
        contactData.value?.phone_number ||
        contactData.value?.phoneNumber ||
        ''
      ).trim() ||
      (contactData.value?.email || '').trim() ||
      (ca.wechat || '').trim() ||
      (ca.qq || '').trim() ||
      (ca.landline || '').trim();
    if (!hasContactMethod) {
      useAlert(
        '标记"未识别已同步"前请至少填写一种联系方式（手机号/邮箱/微信等）',
        'error'
      );
      // 回退本地状态到原值(下拉框回到更新前), 不保存
      const orig =
        props.selectedContact?.custom_attributes ||
        props.selectedContact?.customAttributes ||
        {};
      const origStatus = orig.card_status || orig.cardStatus || '';
      const reverted = {
        ...ca,
        card_status: origStatus,
        cardStatus: origStatus,
      };
      contactData.value = {
        ...contactData.value,
        custom_attributes: reverted,
        customAttributes: reverted,
      };
      return;
    }
  }
  try {
    // 定制: 保存时发"合并后"的 custom_attributes(蛇形打底 + 驼峰覆盖)。
    // 只发驼峰会丢 card_status(驼峰侧不含), 只发蛇形会丢刚填的微信(蛇形是旧值);
    // 合并发送 + 后端 merge, 两边都不丢。
    const { customAttributes, custom_attributes, ...basicContactData } =
      contactData.value;
    const mergedForSave = {
      ...(custom_attributes || {}),
      ...(customAttributes || {}),
    };
    await store.dispatch('contacts/update', {
      ...basicContactData,
      customAttributes: mergedForSave,
    });
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact && props.selectedContact.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="flex flex-col items-stretch w-full gap-8 pb-6">
    <div class="flex flex-col items-start gap-3">
      <Avatar
        :src="avatarSrc || ''"
        :name="selectedContact?.name || ''"
        :size="72"
        allow-upload
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ selectedContact?.name }}
        </h3>
        <div class="flex flex-col gap-1.5">
          <span
            v-if="selectedContact?.identifier"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-user-gear text-n-slate-10 size-4" />
            {{ selectedContact?.identifier }}
          </span>
          <span class="inline-flex items-center gap-1 text-sm text-n-slate-11">
            <span
              v-if="selectedContact?.identifier"
              class="i-ph-activity text-n-slate-10 size-4"
            />
            {{ $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
            •
            {{
              $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                date: lastActivityAt,
              })
            }}
          </span>
          <span
            v-if="location"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-map-pin text-n-slate-10 size-4" />
            {{ location }}
          </span>
        </div>
      </div>
      <ContactLabels :contact-id="selectedContact?.id" />
      <div class="flex items-center gap-2">
        <span class="text-sm text-n-slate-11">名片状态</span>
        <span
          class="inline-block w-3 h-3 rounded-sm flex-shrink-0"
          :class="cardStatusColorClass"
        ></span>
        <select
          :value="selectedCardStatus"
          class="h-8 px-2 text-sm rounded-md border border-n-slate-6 bg-n-alpha-1 dark:bg-n-solid-3 text-n-slate-12 outline-none focus:border-n-brand"
          @change="selectCardStatus($event.target.value)"
        >
          <option
            v-for="opt in cardStatusOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>
    </div>
    <div class="flex flex-col items-start gap-6">
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        is-details-view
        @update="handleFormUpdate"
      />
      <Button
        :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="updateContact"
      />
    </div>
    <Policy :permissions="['administrator']">
      <div
        class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
      >
        <div class="flex flex-col gap-2">
          <h6 class="text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h6>
          <span class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
          color="ruby"
          @click="openConfirmDeleteContactDialog"
        />
      </div>
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>
  </div>
</template>
